



import React, {
  memo,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { toEmojiChar } from "../../../utils/messageNormalizer";

const QUICK_REACTIONS = ["👍", "✔","🔥"];

const ReactionPopover = memo(
  ({
    anchorRef,
    messageId,
    onReaction,
    onPin,
    onDelete,
    isOwn,
    isVisible,
    onClose,
  }) => {
    const popoverRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0, ready: false });

    // ── Compute position relative to anchor ─────────────────────────────────
    useLayoutEffect(() => {
      if (!isVisible || !anchorRef?.current) return;

      const reposition = () => {
        const anchor = anchorRef.current;
        if (!anchor) return;

        const rect = anchor.getBoundingClientRect();
        const popoverH = 52; // approx height of the pill
        const popoverW = 290; // approx width

        // Preferred: just below the anchor
        let top = rect.bottom + 8 + window.scrollY;
        let left = rect.left + rect.width / 2 - popoverW / 2 + window.scrollX;

        // Clamp left to stay inside viewport
        const margin = 8;
        left = Math.max(
          margin,
          Math.min(left, window.innerWidth - popoverW - margin),
        );

        // If below would overflow viewport bottom, flip to above
        if (rect.bottom + 8 + popoverH > window.innerHeight) {
          top = rect.top - 8 - popoverH + window.scrollY;
        }

        setCoords({ top, left, ready: true });
      };

      reposition();
      window.addEventListener("resize", reposition);
      window.addEventListener("scroll", reposition, true);
      return () => {
        window.removeEventListener("resize", reposition);
        window.removeEventListener("scroll", reposition, true);
      };
    }, [isVisible, anchorRef]);

    // ── Close on Escape ───────────────────────────────────────────────────────
    useEffect(() => {
      if (!isVisible) return;
      const handleKey = (e) => {
        if (e.key === "Escape") onClose();
      };
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    }, [isVisible, onClose]);

    // ── Close on outside click ────────────────────────────────────────────────
    useEffect(() => {
      if (!isVisible) return;
      const handleOutside = (e) => {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(e.target) &&
          anchorRef?.current &&
          !anchorRef.current.contains(e.target)
        ) {
          onClose();
        }
      };
      // Slight delay so the opening click doesn't immediately close it
      const id = setTimeout(() => {
        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("touchstart", handleOutside);
      }, 50);
      return () => {
        clearTimeout(id);
        document.removeEventListener("mousedown", handleOutside);
        document.removeEventListener("touchstart", handleOutside);
      };
    }, [isVisible, onClose, anchorRef]);

    if (!isVisible) return null;

    const handleReaction = (emoji) => {
      onReaction?.(emoji);
      onClose();
    };

    const handlePin = () => {
      onPin?.();
      onClose();
    };

    const handleDelete = () => {
      onDelete?.();
      onClose();
    };

    const popover = (
      <div
        ref={popoverRef}
        style={{
          position: "fixed",
          top: coords.top,
          left: coords.left,
          zIndex: 99999,
          opacity: coords.ready ? 1 : 0,
          transform: coords.ready ? "scale(1)" : "scale(0.92)",
          transition: "opacity 150ms ease-out, transform 150ms ease-out",
          pointerEvents: "auto",
        }}
        role="dialog"
        aria-label="Reaction options"
        aria-modal="false"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-1 bg-[#1A1A2E]/98 backdrop-blur-xl border border-white/10 rounded-full px-2 py-1.5 shadow-2xl">
          {/* Quick emoji reactions */}
          {QUICK_REACTIONS.map((emoji) => {
            const displayEmoji = toEmojiChar(emoji);
            return (
              <button
                key={emoji}
                type="button"
                className="w-9 h-9 flex items-center justify-center text-xl rounded-full transition-all duration-150 hover:scale-125 active:scale-95 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                onClick={() => handleReaction(displayEmoji)}
                aria-label={`React with ${displayEmoji}`}
                title={displayEmoji}
              >
                {/* ✅ FIX: always render converted emoji character */}
                {displayEmoji}
              </button>
            );
          })}

          {/* Divider */}
          <div className="w-px h-5 bg-white/10 mx-0.5" />

          {/* Pin */}
          <button
            type="button"
            className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-150 hover:bg-white/10 hover:text-yellow-400 text-black focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            onClick={handlePin}
            aria-label="Pin message"
            title="Pin"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
              />
            </svg>
          </button>

          {/* Delete — own messages only */}
          {isOwn && (
            <button
              type="button"
              className="w-9 h-9 flex items-center justify-center rounded-full transition-all duration-150 hover:bg-white/10 hover:text-red-400 text-red focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              onClick={handleDelete}
              aria-label="Delete message"
              title="Delete"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    );

    return createPortal(popover, document.body);
  },
);

ReactionPopover.displayName = "ReactionPopover";
export default ReactionPopover;
